import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShieldCheck, Euro, ArrowRightLeft, Link2, Users, HandCoins } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/6e4fba8f-fe4c-4e25-a28c-f4fefa54fbfa/images/8129009579c003ef5e4a280a2bfb70153af3cf6f1f474bbfa490300bee17471c.png";
const CITY_IMG = "https://images.pexels.com/photos/35369378/pexels-photo-35369378.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ROOM_IMG = "https://images.unsplash.com/photo-1615874959474-d609969a20ed?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";

export default function Landing() {
    return (
        <div data-testid="landing-page">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-20">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-7 fade-up">
                            <div className="inline-flex items-center gap-2 bg-muted rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                                <Sparkles className="w-3 h-3 text-primary" />
                                For Erasmus & Exchange Students
                            </div>
                            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-display leading-[1.05]">
                                Swap your room.
                                <br />
                                <span className="text-primary">Start a new adventure.</span>
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                                Heading to Barcelona next semester? There's someone from Barcelona heading to your city. Swap rooms for the exact period you're both away. Same dates, fair rent, zero double payments.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/register">
                                    <Button data-testid="hero-register-btn" size="lg" className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7">
                                        Get started free <ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </Link>
                                <Link to="/browse">
                                    <Button data-testid="hero-browse-btn" size="lg" variant="outline" className="rounded-full h-12 px-7 border-border">
                                        Browse rooms
                                    </Button>
                                </Link>
                            </div>
                            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-secondary" /> Built by students, for students</div>
                                <div className="flex items-center gap-2"><Euro className="w-4 h-4 text-secondary" /> Fair rent split</div>
                                <div className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-secondary" /> Smart matching</div>
                            </div>
                        </div>
                        <div className="md:col-span-5 relative fade-up delay-1">
                            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-xl">
                                <img src={HERO_IMG} alt="Exchange students" className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute -bottom-8 -left-8 bg-white border border-border rounded-2xl p-4 shadow-lg hidden sm:flex items-center gap-3 w-64">
                                <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                                    <ArrowRightLeft className="w-5 h-5 text-secondary" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">New match</div>
                                    <div className="text-sm font-medium">Amsterdam ↔ Lisbon</div>
                                </div>
                            </div>
                            <div className="absolute -top-6 -right-6 bg-accent text-accent-foreground rounded-2xl p-3 shadow hidden sm:block">
                                <div className="text-xs font-bold uppercase tracking-wider">Difference</div>
                                <div className="text-xl font-bold font-display">€ 85/mo</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Mirror Match */}
            <section className="bg-background border-y border-border/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                    <div className="max-w-2xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">The mirror match</div>
                        <h2 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">
                            Same dates.{" "}
                            <span className="text-primary">Opposite cities.</span>
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                            You're going there. They're coming here. Swap rooms for the exact same period. Your room is their dream, theirs is yours.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-4 items-stretch">
                        {/* Sofia card */}
                        <div data-testid="mirror-card-a" className="bg-white border border-border/60 rounded-2xl p-6 lg:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-display text-lg font-bold">
                                    S
                                </div>
                                <div>
                                    <div className="font-semibold">Sofia, 22</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Stockholm 🇸🇪</div>
                                </div>
                            </div>
                            <div className="mt-6 pt-5 border-t border-border/50">
                                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Going to</div>
                                <div className="font-display text-xl font-semibold mt-1">Barcelona 🇪🇸</div>
                            </div>
                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Period</div>
                                    <div className="font-medium mt-0.5">Sep to Jan</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Her rent</div>
                                    <div className="font-medium mt-0.5">€620 / mo</div>
                                </div>
                            </div>
                        </div>

                        {/* Swap badge */}
                        <div className="flex md:flex-col items-center justify-center gap-3 py-2 md:py-0 md:px-2">
                            <div className="w-px md:h-12 h-px md:w-px bg-border/60 md:hidden" />
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                                    <ArrowRightLeft className="w-7 h-7" strokeWidth={2.2} />
                                </div>
                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-1 whitespace-nowrap">
                                    Match
                                </div>
                            </div>
                            <div className="hidden md:block text-center mt-6 bg-accent/40 rounded-xl px-4 py-2">
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Difference</div>
                                <div className="font-display font-bold text-foreground">€ 160 / mo</div>
                            </div>
                        </div>

                        {/* Diego card */}
                        <div data-testid="mirror-card-b" className="bg-white border border-border/60 rounded-2xl p-6 lg:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-secondary text-secondary-foreground flex items-center justify-center font-display text-lg font-bold">
                                    D
                                </div>
                                <div>
                                    <div className="font-semibold">Diego, 23</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Barcelona 🇪🇸</div>
                                </div>
                            </div>
                            <div className="mt-6 pt-5 border-t border-border/50">
                                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Going to</div>
                                <div className="font-display text-xl font-semibold mt-1">Stockholm 🇸🇪</div>
                            </div>
                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Period</div>
                                    <div className="font-medium mt-0.5">Sep to Jan</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">His rent</div>
                                    <div className="font-medium mt-0.5">€460 / mo</div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile-only difference pill */}
                        <div className="md:hidden bg-accent/40 rounded-xl px-4 py-3 text-center">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Difference</span>
                            <span className="font-display font-bold text-foreground ml-2">€ 160 / mo</span>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                            <span>Both stay in someone's actual home</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                            <span>Pay only the rent difference, fairly split</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-foreground" />
                            <span>No double rent, no tourist prices</span>
                        </div>
                    </div>

                    {/* Chain swap teaser (subtle, secondary feature) */}
                    <div className="mt-12 pt-8 border-t border-dashed border-border/60">
                        <div className="max-w-4xl">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-2xl bg-secondary/15 flex items-center justify-center shrink-0">
                                    <Link2 className="w-5 h-5 text-secondary" strokeWidth={2.2} />
                                </div>
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-secondary">Also possible: chain swap</div>
                                    <h3 className="font-display text-xl font-semibold mt-0.5">
                                        Three students, one circle.
                                    </h3>
                                </div>
                            </div>

                            <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-2xl">
                                When a direct match isn't available, we connect three students in a loop. Each one stays in someone else's place, and your room is filled too. Your Matches page shows both options automatically.
                            </p>

                            {/* Visual chain: 3 city tiles with arrows in between, loop arrow back */}
                            <div className="mt-6" data-testid="chain-mini-visual">
                                <div className="flex flex-wrap items-center justify-start gap-1.5 sm:gap-2">
                                    <CityTile letter="P" name="Prague" tint="primary" />
                                    <ChainArrow />
                                    <CityTile letter="B" name="Barcelona" tint="secondary" />
                                    <ChainArrow />
                                    <CityTile letter="A" name="Amsterdam" tint="foreground" />
                                    <ChainArrow loop />
                                    <div className="text-xs text-muted-foreground italic">back to Prague</div>
                                </div>
                                <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                                    Same dates window
                                    <span className="text-border">·</span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                    Everyone's room is filled
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Exchange Buddy — a prominent second-tier feature */}
            <section className="bg-primary text-primary-foreground py-20" data-testid="landing-buddy-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <div className="inline-flex items-center gap-2 bg-primary-foreground/15 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em]">
                            <Users className="w-3 h-3" /> Exchange Buddy
                        </div>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                            No swap? <span className="opacity-80">Still find your</span> <br/>Exchange Buddy.
                        </h2>
                        <p className="mt-5 text-lg text-primary-foreground/85 max-w-xl leading-relaxed">
                            Swap information instead of rooms and skip <strong>agency fees</strong> most landlords charge new arrivals. Match with someone moving in the opposite direction — they know your new city inside out, and you know theirs. Help each other. Save each other money.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/exchange-buddy">
                                <Button data-testid="landing-buddy-cta" size="lg" className="rounded-full h-12 px-7 bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                                    How it works <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                            <Link to="/register">
                                <Button data-testid="landing-buddy-signup" variant="outline" size="lg" className="rounded-full h-12 px-7 border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
                                    Sign up free
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-5 border border-primary-foreground/20">
                                <HandCoins className="w-6 h-6" />
                                <div className="font-display text-xl font-semibold mt-3">Save on agency fees</div>
                                <div className="text-sm text-primary-foreground/80 mt-1">Save on the move.</div>
                            </div>
                            <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-5 border border-primary-foreground/20 mt-8">
                                <Users className="w-6 h-6" />
                                <div className="font-display text-xl font-semibold mt-3">±6 months window</div>
                                <div className="text-sm text-primary-foreground/80 mt-1">No need to match exact dates.</div>
                            </div>
                            <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-5 border border-primary-foreground/20">
                                <ArrowRightLeft className="w-6 h-6" />
                                <div className="font-display text-xl font-semibold mt-3">Both directions</div>
                                <div className="text-sm text-primary-foreground/80 mt-1">They live where you're going, and vice versa.</div>
                            </div>
                            <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-5 border border-primary-foreground/20 mt-8">
                                <Sparkles className="w-6 h-6" />
                                <div className="font-display text-xl font-semibold mt-3">No room needed</div>
                                <div className="text-sm text-primary-foreground/80 mt-1">Just tips. Just help.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="max-w-2xl">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">How it works</div>
                    <h2 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Three steps to your next home</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {[
                        { n: "01", t: "List your room", d: "Share location, dates, rent and photos. Tell us where you're heading.", icon: "MapPin" },
                        { n: "02", t: "Find your match", d: "We pair you with students coming to your city and leaving from your destination.", icon: "ArrowRightLeft" },
                        { n: "03", t: "Swap fairly", d: "See the rent difference at a glance and lock in details through in-app chat.", icon: "Euro" },
                    ].map((s, i) => (
                        <div key={s.n} data-testid={`how-step-${i}`} className="bg-white border border-border/60 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">{s.n}</div>
                            <h3 className="font-display text-xl font-semibold">{s.t}</h3>
                            <p className="text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Visual band */}
            <section className="bg-muted py-20 mt-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-12 gap-6 items-center">
                        <div className="md:col-span-7 grid grid-cols-2 gap-4">
                            <img src={CITY_IMG} alt="European city" className="rounded-2xl aspect-[4/5] object-cover" />
                            <img src={ROOM_IMG} alt="Room interior" className="rounded-2xl aspect-[4/5] object-cover mt-10" />
                        </div>
                        <div className="md:col-span-5">
                            <h2 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">
                                Affordable travel, <span className="text-secondary">real cities.</span>
                            </h2>
                            <p className="mt-4 text-muted-foreground leading-relaxed">
                                No double rent. No tourist prices. Swap with a student taking the opposite route. Same room, same dates, fair split.
                            </p>
                            <Link to="/register">
                                <Button data-testid="band-cta-btn" className="mt-6 rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-7">
                                    Create your profile <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-foreground text-background py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between gap-8">
                    <div>
                        <div className="font-display text-2xl font-bold">Erasmus<span className="text-primary">Swap</span></div>
                        <p className="text-background/60 mt-2 max-w-sm">A community for exchange students. Made by students, for students.</p>
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-background/40">© {new Date().getFullYear()} ErasmusSwap</div>
                </div>
            </footer>
        </div>
    );
}

function CityTile({ letter, name, tint }) {
    const tintMap = {
        primary: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        foreground: "bg-foreground text-background",
    };
    return (
        <div className="inline-flex items-center gap-2 bg-white border border-border/60 rounded-full pl-1 pr-3 py-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${tintMap[tint] || tintMap.primary}`}>
                {letter}
            </div>
            <span className="text-sm font-medium">{name}</span>
        </div>
    );
}

function ChainArrow({ loop = false }) {
    return (
        <div className="inline-flex items-center text-muted-foreground/70" aria-hidden="true">
            {loop ? (
                <svg width="34" height="20" viewBox="0 0 34 20" fill="none">
                    <path d="M2 4 Q 17 -4 32 4 L 28 1 M 32 4 L 28 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
            ) : (
                <svg width="22" height="14" viewBox="0 0 22 14" fill="none">
                    <path d="M2 7 L 18 7 M 18 7 L 13 2 M 18 7 L 13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
        </div>
    );
}

function ChainMiniCard({ initial, name, dest, color }) {
    const colorMap = {
        primary: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        foreground: "bg-foreground text-background",
    };
    return (
        <div className="bg-white border border-border/60 rounded-xl p-3 flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
                <div className={`w-7 h-7 rounded-lg shrink-0 flex items-center justify-center font-bold text-xs ${colorMap[color] || colorMap.primary}`}>
                    {initial}
                </div>
                <div className="text-xs font-semibold truncate">{name}</div>
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Goes to</div>
            <div className="text-xs font-medium truncate">{dest}</div>
        </div>
    );
}
